'use client';

import { useCallback, useRef } from 'react';
import Env from '../../environmentVariables/EnvironmentVariables';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { SANITY_CHECK_ABS_MAX_CU_PRICE } from '../../utils/PriorityFeeCalculator';
import { FEE_STRATEGY_TARGET_PERCENTILE } from './consts';
import useMarketsForDlobToTrack, {
	MarketStatePriority,
} from '../../hooks/useMarketsForDlobToTrack';
import {
	getHeliusPriorityFeeEstimate,
	MarketKey,
} from '@velocity-exchange/common';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { useImmediateInterval } from 'src/hooks/useImmediateInterval';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

const FALLBACK_TYPE: 'max' | 'min' = 'max';

const EXTRA_POLLING_MULTIPLIER = Env.feeSubscriptionPollingMultiplier;

const FEE_ESTIMATE_ENDPOINT_IS_AVAILABLE = { current: true };

const useHeliusSourcedPriorityFees = () => {
	const selectMarket =
		useMarketsForDlobToTrack().categories[MarketStatePriority.SelectedMarket];
	const otherMarkets =
		useMarketsForDlobToTrack().categories[MarketStatePriority.BackgroundDeep];
	const walletIsConnected = useWalletIsConnected();
	const marketsToPoll = [...(selectMarket ?? []), ...(otherMarkets ?? [])];
	const marketsToPollRef = useSyncedRef(marketsToPoll);

	const latestFeeResults = useRef<Record<MarketKey | 'fallback', number>>({
		fallback: 0,
	});

	const pollingMultiplier = useDriftStore((s) => s.pollingMultiplier);
	const pollMs =
		Env.pollingFrequencyMs *
		pollingMultiplier *
		(EXTRA_POLLING_MULTIPLIER ?? 0);

	const refreshHeliusPriorityFeeValue = useCallback(async () => {
		if (!FEE_ESTIMATE_ENDPOINT_IS_AVAILABLE.current || !walletIsConnected)
			return;

		const marketsToFetch = marketsToPollRef.current;

		const feeLevelToUse =
			FEE_STRATEGY_TARGET_PERCENTILE > 75
				? 'veryHigh'
				: FEE_STRATEGY_TARGET_PERCENTILE > 50
					? 'high'
					: FEE_STRATEGY_TARGET_PERCENTILE > 25
						? 'medium'
						: 'low';

		const results = await getHeliusPriorityFeeEstimate(
			marketsToFetch,
			feeLevelToUse
		);

		if (!results) {
			return;
		}

		const fallbackResult =
			FALLBACK_TYPE === 'max' ? Math.max(...results) : Math.min(...results);

		latestFeeResults.current.fallback = fallbackResult;

		results.forEach((heliusResult, index) => {
			const marketId = marketsToFetch[index];
			if (!heliusResult) {
				// Use undefined here, because Math.min can turned into NaN otherwise.
				latestFeeResults.current[marketId.key] = undefined as unknown as number;
			} else {
				latestFeeResults.current[marketId.key] = Math.min(
					heliusResult,
					SANITY_CHECK_ABS_MAX_CU_PRICE
				);
			}
		});
	}, [walletIsConnected]);

	useImmediateInterval({
		callback: refreshHeliusPriorityFeeValue,
		delayMs: pollMs,
		runOnCallbackChange: true,
	});

	return latestFeeResults;
};

export default useHeliusSourcedPriorityFees;
